import { ReactNode, useMemo } from "react";
import { Badge, BadgeSize, BadgeVariant } from "shared/components/Badge";
import { OpenReverificationRequest, ScreeningStatus } from "shared/types";
import { screeningStatusTitle } from "./title";
import { cx } from "shared/utils";
import { formatReverificationRequest } from "./reverification";

const ReverificationTooltip = ({
  requests
}: {
  requests: OpenReverificationRequest[];
}) => (
  <ul>
    {requests.map((request) => (
      <li
        key={`${request.kind}-${request.applicant_id}-${request.verification_id}-${request.request_id ?? ""}`}
        className="my-1 ml-5 list-disc text-xs"
      >
        {formatReverificationRequest(request)}
      </li>
    ))}
  </ul>
);

export const ScreeningStatusBadge = ({
  status,
  paused,
  className,
  reverificationRequests
}: {
  status?: string;
  // A failed premium warnings report pauses the remaining paid reports; the
  // screening reads as paused rather than failed until someone overrides it.
  paused?: boolean;
  className?: string;
  reverificationRequests?: OpenReverificationRequest[];
}) => {
  const {
    variant,
    title,
    tooltip
  }: { variant: BadgeVariant; title: string; tooltip?: ReactNode } =
    useMemo(() => {
      if (paused) return { variant: BadgeVariant.WARNING, title: "Paused" };

      switch (status) {
        case ScreeningStatus.Processing:
          return {
            variant: BadgeVariant.WARNING,
            title: screeningStatusTitle(status)
          };
        case ScreeningStatus.Reverifying:
          return {
            variant: BadgeVariant.WARNING,
            title: screeningStatusTitle(status),
            tooltip: reverificationRequests?.length ? (
              <ReverificationTooltip requests={reverificationRequests} />
            ) : undefined
          };
        case ScreeningStatus.Completed:
          return {
            variant: BadgeVariant.SUCCESS,
            title: screeningStatusTitle(status)
          };
        case ScreeningStatus.Failed:
          return {
            variant: BadgeVariant.ERROR,
            title: screeningStatusTitle(status)
          };
        case ScreeningStatus.NotStarted:
        default:
          return {
            variant: BadgeVariant.BASE,
            title: screeningStatusTitle(status)
          };
      }
    }, [status, paused, reverificationRequests]);

  return (
    <Badge
      className={cx("shrink-0 items-center", className)}
      variant={variant}
      size={BadgeSize.SM}
      tooltip={tooltip}
      tooltipClassName="z-[11]"
    >
      {title}
    </Badge>
  );
};
